import React, { useEffect, useRef, useMemo } from 'react';
import { StyleSheet, View, Pressable, Platform, Animated } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import useResponsiveness from '@/hooks/useResponsiveness';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import { useUIStore } from '@/store';
import PropTypes from 'prop-types';

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  container: {
    marginTop: 0,
    marginBottom: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    paddingHorizontal: isXS ? 15 : 20,
    width: '100%',
    maxWidth: 
      messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW : 
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
  },
  suggestion: {
    backgroundColor: colors.solid_color_block,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    flexShrink: 1,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        maxWidth: '100%',
      },
    }),
  },
  suggestionHovered: {
    backgroundColor: colors.solid_color_secondary,
    borderColor: colors.border_color_input,
  },
  suggestionPressed: {
    opacity: 0.8,
  },
  suggestionText: {
    fontSize: 13,
    lineHeight: 18,
    color: colors.text_color_secondary,
    ...Platform.select({
      web: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    }),
  },
});

const SuggestionPill = React.memo(({ text, onPress, colors, styles }) => {
  // Truncate long text
  const displayText = text.length > 80 ? `${text.substring(0, 77)}...` : text;

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.suggestion,
        hovered && styles.suggestionHovered,
        pressed && styles.suggestionPressed,
      ]}
      onPress={() => onPress(text)}
      accessibilityRole="button"
      accessibilityLabel={`Suggestion: ${text}`}
      accessibilityHint="Tap to send this suggestion"
    >
      <Text
        style={styles.suggestionText}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {displayText}
      </Text>
    </Pressable>
  );
});

SuggestionPill.propTypes = {
  text: PropTypes.string.isRequired,
  onPress: PropTypes.func.isRequired,
  colors: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
};

SuggestionPill.displayName = 'SuggestionPill';

function FollowUpSuggestions({ suggestions, onSuggestionPress }) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const messageWidth = useUIStore(state => state.messageWidth);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  
  const styles = useMemo(() => createStyles(colors, isXS, messageWidth), [colors, isXS, messageWidth]);

  // Fade in animation
  useEffect(() => {
    fadeAnim.setValue(0);
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [suggestions]);

  // Don't render if no valid suggestions
  if (!suggestions || !Array.isArray(suggestions) || suggestions.length === 0) {
    return null;
  }

  // Filter out empty suggestions and limit to 1
  const validSuggestions = suggestions.filter(s => s?.text?.trim()).slice(0, 1);
  
  if (validSuggestions.length === 0) {
    return null;
  }

  return (
    <Animated.View
      style={[
        styles.container,
        {
          opacity: fadeAnim,
          transform: [{
            translateY: fadeAnim.interpolate({
              inputRange: [0, 1],
              outputRange: [8, 0],
            }),
          }],
        },
      ]}
      accessibilityRole="toolbar"
      accessibilityLabel="Follow-up suggestions"
    >
      {validSuggestions.map((suggestion, index) => (
        <SuggestionPill
          key={`${suggestion.text}-${index}`}
          text={suggestion.text}
          onPress={onSuggestionPress}
          colors={colors}
          styles={styles}
        />
      ))}
    </Animated.View>
  );
}

FollowUpSuggestions.propTypes = {
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
    })
  ),
  onSuggestionPress: PropTypes.func.isRequired,
};

FollowUpSuggestions.displayName = 'FollowUpSuggestions';

export default React.memo(FollowUpSuggestions);
